import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Rocket, Users, ShieldCheck, Coins } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { useAudio, useTrack } from "@/context/GameFeel";

export default function LiveCrash() {
  const { user, setUser } = useAuth();
  const { t } = useT();
  const { react, sfx } = useAudio();
  useTrack("play");
  const [state, setState] = useState(null);
  const [bet, setBet] = useState(100);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);
  const lastPhase = useRef(null);

  useEffect(() => {
    const poll = async () => {
      try {
        const { data } = await api.get("/crash-live/state");
        setState(data);
        if (lastPhase.current === "running" && data.phase === "crashed" && data.my_bet && data.my_bet.cashed_out == null) {
          toast.error(`💥 ${t("crashed")} @ ${data.crash_point}x`);
          react({ outcome: "loss", game: "crash" });
        }
        if (lastPhase.current === "waiting" && data.phase === "running") sfx("whoosh");
        lastPhase.current = data.phase;
      } catch { /* ignore */ }
    };
    poll();
    pollRef.current = setInterval(poll, 250);
    return () => clearInterval(pollRef.current);
  }, [t]);

  const placeBet = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/crash-live/bet", { bet });
      sfx("tap");
      setUser((u) => ({ ...u, coins: data.coins }));
      toast.success(`Coins in · ${bet.toLocaleString()} coins`);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const cashOut = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/crash-live/cashout");
      setUser((u) => ({ ...u, coins: data.coins }));
      toast.success(`${t("cash_out")} @ ${data.multiplier}x · +${data.payout.toLocaleString()}`);
      react({ outcome: "win", game: "crash", big: data.multiplier >= 5 });
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const phase = state?.phase;
  const m = state?.multiplier ?? 1;
  const myBet = state?.my_bet;
  const canBet = phase === "waiting" && !myBet;
  const canCash = phase === "running" && myBet && myBet.cashed_out == null;
  const color = phase === "crashed" ? "#EF4444" : phase === "running" ? "#F59E0B" : "#9CA3AF";

  return (
    <div className="p-4 lg:p-8">
      <Link to="/casino" className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors duration-200 mb-4">
        <ArrowLeft className="w-4 h-4" /> {t("nav_casino")}
      </Link>
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-black flex items-center gap-2"><Rocket className="w-7 h-7 text-amber-400" /> {t("live_crash")}</h1>
          <p className="text-gray-400 flex items-center gap-2"><Users className="w-4 h-4" /> {state?.player_count ?? 0} real {state?.player_count === 1 ? "player" : "players"} this round · no bots</p>
        </div>
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs font-semibold">
          <ShieldCheck className="w-3.5 h-3.5" /> {t("coins_only_no_cash")}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="glass rounded-3xl border border-amber-500/20 glow-coins p-8 h-[380px] flex flex-col items-center justify-center relative overflow-hidden">
            {/* history strip */}
            <div className="absolute top-4 left-0 right-0 flex gap-1.5 justify-center flex-wrap px-4">
              {(state?.history || []).slice(0, 12).map((h, i) => (
                <span key={i} className={`text-xs font-mono px-2 py-0.5 rounded ${h < 2 ? "bg-red-500/20 text-red-300" : h < 5 ? "bg-amber-500/20 text-amber-300" : "bg-emerald-500/20 text-emerald-300"}`}>{h}x</span>
              ))}
            </div>
            <motion.div animate={{ y: phase === "running" ? [0, -6, 0] : 0 }} transition={{ repeat: Infinity, duration: 0.3 }}>
              <Rocket className="w-14 h-14 mb-3" style={{ color, transform: phase === "crashed" ? "rotate(90deg)" : "rotate(-45deg)" }} />
            </motion.div>
            <div className="font-mono font-black text-6xl sm:text-7xl" style={{ color }}>{m.toFixed(2)}x</div>
            <p className="text-gray-400 mt-3 h-6">
              {phase === "waiting" && `${t("waiting_bets")} · ${t("next_round")} ${state?.time_left ?? ""}s`}
              {phase === "running" && t("running")}
              {phase === "crashed" && `${t("crashed")} @ ${state?.crash_point}x · ${t("next_round")} ${state?.time_left ?? ""}s`}
            </p>
            {myBet && (
              <div className="mt-2 text-sm">
                <span className="text-gray-500">Your coins: </span>
                <span className="font-mono text-amber-300">{myBet.bet.toLocaleString()}</span>
                {myBet.cashed_out != null && <span className="text-emerald-400 ml-2 font-mono">cashed @ {myBet.cashed_out}x ✓</span>}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4">
          {canCash ? (
            <button data-testid="live-crash-cashout" onClick={cashOut} disabled={busy}
              className="w-full py-6 rounded-2xl bg-gradient-to-r from-emerald-500 to-cyan-600 font-black text-2xl hover:opacity-90 transition-opacity duration-200 pulse-ring disabled:opacity-50">
              {t("cash_out")} · {(myBet.bet * m).toFixed(0)}
            </button>
          ) : (
            <div className="glass rounded-2xl p-5 border border-white/10">
              <label className="text-sm text-gray-400 mb-2 block">{t("stake")} ({t("coins")})</label>
              <div className="flex items-center gap-2 mb-3">
                <Coins className="w-5 h-5 text-amber-400" />
                <input data-testid="live-crash-bet-input" type="number" min={10} value={bet}
                  onChange={(e) => setBet(Math.max(10, parseInt(e.target.value) || 0))}
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 font-mono focus:border-amber-500 outline-none transition-colors duration-200" />
              </div>
              <div className="flex flex-wrap gap-2 mb-4">
                {[-100, 100, "2x", "1/2"].map((q) => (
                  <button key={q} onClick={() => setBet((b) => q === "2x" ? b * 2 : q === "1/2" ? Math.max(10, Math.floor(b / 2)) : Math.max(10, b + q))}
                    className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs hover:bg-white/10 transition-colors duration-200">
                    {typeof q === "number" ? (q > 0 ? `+${q}` : q) : q}
                  </button>
                ))}
              </div>
              <button data-testid="live-crash-bet" onClick={placeBet} disabled={!canBet || busy}
                className="w-full py-3.5 rounded-xl bg-gradient-to-r from-amber-500 to-purple-600 font-bold text-lg hover:opacity-90 transition-opacity duration-200 disabled:opacity-40">
                {myBet ? "Coins in ✓" : phase === "waiting" ? t("place_bet") : "Wait for next round"}
              </button>
            </div>
          )}

          <div className="glass rounded-2xl p-5 border border-white/10">
            <p className="text-sm font-semibold mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-amber-400" /> {t("players_round")} · {state?.player_count ?? 0}</p>
            <div className="space-y-1.5 max-h-52 overflow-y-auto hide-scroll">
              {(state?.players || []).map((p, i) => (
                <div key={i} className={`flex justify-between items-center text-sm px-2 py-1 rounded-lg ${p.you ? "bg-amber-500/10" : ""}`}>
                  <span className="text-gray-300 truncate">{p.name}{p.you ? " (you)" : ""}</span>
                  <span className="flex items-center gap-2">
                    <span className="font-mono text-gray-500 text-xs">{p.bet.toLocaleString()}</span>
                    {p.cashed_out != null
                      ? <span className="font-mono text-emerald-400 text-xs">✓ {p.cashed_out}x</span>
                      : phase === "running"
                        ? <span className="font-mono text-amber-400 text-xs animate-pulse">live</span>
                        : phase === "crashed"
                          ? <span className="font-mono text-red-400 text-xs">✗</span>
                          : <span className="font-mono text-gray-600 text-xs">in</span>}
                  </span>
                </div>
              ))}
              {(state?.players || []).length === 0 && <p className="text-gray-600 text-sm">No one in yet this round — real players only, no simulated players.</p>}
            </div>
          </div>

          <div className="glass rounded-2xl p-5 border border-white/10">
            <p className="text-sm font-semibold mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-emerald-400" /> {t("live_cashouts")}</p>
            <div className="space-y-1.5 max-h-40 overflow-y-auto hide-scroll">
              {(state?.recent_cashouts || []).slice().reverse().map((c, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span className="text-gray-300">{c.name}</span>
                  <span className="font-mono text-emerald-400">{c.multiplier}x · +{c.payout.toLocaleString()}</span>
                </div>
              ))}
              {(state?.recent_cashouts || []).length === 0 && <p className="text-gray-600 text-sm">No one has locked in yet this round.</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
